"use client";

import { useMemo } from "react";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useFirestoreCollection } from "@/hooks/useFirestore";
import type { HouseholdChat } from "@/lib/models";

/**
 * Returns the number of chats with unread messages for the current user.
 */
export function useUnreadChatsCount(): number {
  const { householdId, profile } = useHouseholdContext();

  const { data: chats } = useFirestoreCollection<HouseholdChat>(
    householdId ? `households/${householdId}/chats` : null
  );

  return useMemo(() => {
    const uid = profile?.uid;
    if (!uid) return 0;
    return chats.filter((chat) => {
      // Not unread if the current user sent the last reply
      if (chat.last_reply_uid === uid) return false;
      // Not unread if there's no last reply
      if (!chat.last_reply_uid) return false;
      // Unread if last_reply_read_by doesn't include the user
      const readBy = chat.last_reply_read_by ?? [];
      return !readBy.includes(uid);
    }).length;
  }, [chats, profile?.uid]);
}
